import React, { useEffect, useState } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDown, ArrowUp, Clock, Info, Radio, TrendingUp, Zap } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { formatAirDate } from '@/lib/mixDates';

const ET = 'America/New_York';

const fmtTime = (iso) =>
  new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));

const fmtDay = (iso) =>
  new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(iso));

function countdown(ms) {
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const countryName = (code) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

function SongRows({ rows, positive }) {
  const { t } = useI18n();
  if (!rows || rows.length === 0) return <p className="text-neutral-400 text-sm">{t('Nothing stood out.')}</p>;
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li key={i} className="flex items-center gap-3 text-sm">
          <div className="min-w-0 flex-1">
            <p className="text-black font-semibold truncate">{r.song}</p>
            <p className="text-neutral-400 text-xs truncate">{r.artist}</p>
          </div>
          <span className={`shrink-0 text-xs font-bold ${positive ? 'text-green-600' : 'text-red-500'}`}>
            {r.net > 0 ? '+' : ''}{r.net}
          </span>
        </li>
      ))}
    </ul>
  );
}

function useNow(everyMs = 30000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

function Delta({ now, before }) {
  if (before == null || !before) return null;
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return <span className="text-xs text-neutral-400">same as last time</span>;
  const up = pct > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-green-600' : 'text-red-500'}`}>
      <Icon size={12} />
      {Math.abs(pct)}% vs previous show
    </span>
  );
}

function Tile({ label, value, sub, children }) {
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4">
      <p className="text-neutral-400 text-[11px] font-bold uppercase tracking-wide">{label}</p>
      <p className="text-black text-2xl font-black mt-1">{value}</p>
      {sub && <p className="text-neutral-400 text-xs mt-0.5">{sub}</p>}
      {children}
    </div>
  );
}

// Big "you are on air" card with the current connection count, or the countdown to the next show.
export function LiveNowCard({ stats, loading }) {
  const { t } = useI18n();
  const now = useNow(30000);
  if (loading && !stats) {
    return <div className="rounded-2xl bg-neutral-900 h-40 animate-pulse mb-6" />;
  }
  const live = stats?.live;
  const next = stats?.next;

  if (live?.on_air) {
    const left = new Date(live.end_iso).getTime() - now;
    const chart = (live.series || []).map((p) => ({ time: fmtTime(p.t), n: p.n }));
    return (
      <div className="rounded-2xl bg-neutral-900 text-white p-5 md:p-6 mb-6 overflow-hidden">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
            <span className="text-xs font-black tracking-widest text-red-400">{t('ON AIR NOW')}</span>
            <span className="text-white/60 text-sm">· {live.show_name}</span>
          </div>
          <span className="text-white/50 text-xs flex items-center gap-1">
            <Clock size={12} /> {fmtTime(live.start_iso)} – {fmtTime(live.end_iso)} ET · {countdown(left)} {t('left')}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-end">
          <div>
            <p className="text-white/50 text-xs font-bold uppercase tracking-wide">{t('Connected right now')}</p>
            <p className="text-6xl md:text-7xl font-black leading-none mt-1">{live.current}</p>
            <p className="text-white/60 text-sm mt-4">
              <span className="font-black text-white text-lg">{live.connections_so_far}</span> {t('total connections so far this show')}
            </p>
          </div>
          <div className="h-28 md:h-32 -mx-2">
            {chart.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chart} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="liveFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" hide />
                  <YAxis hide domain={[0, 'dataMax + 2']} />
                  <Tooltip
                    contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, color: '#fff', fontSize: 12 }}
                    formatter={(v) => [v, t('Connections')]}
                  />
                  <Area type="monotone" dataKey="n" stroke="#c084fc" strokeWidth={2} fill="url(#liveFill)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-white/40 text-sm h-full flex items-center">{t('The chart fills in as your show goes on.')}</p>
            )}
          </div>
        </div>
        {live.stale && (
          <p className="text-amber-300 text-xs mt-3">{t('Live numbers are a few minutes behind right now.')}</p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-neutral-900 text-white p-5 md:p-6 mb-6 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <Radio size={20} className="text-purple-300" />
        </div>
        {next ? (
          <div className="min-w-0">
            <p className="text-white/50 text-xs font-bold uppercase tracking-wide">{t('Your next show')}</p>
            <p className="font-black text-lg truncate">{next.show_name}</p>
            <p className="text-white/60 text-sm">
              {fmtDay(next.start_iso)} · {fmtTime(next.start_iso)} – {fmtTime(next.end_iso)} ET
            </p>
          </div>
        ) : (
          <p className="text-white/60 text-sm">{t('No upcoming shows scheduled.')}</p>
        )}
      </div>
      {next && (
        <div className="text-right">
          <p className="text-white/40 text-xs font-bold uppercase tracking-wide">{t('Starts in')}</p>
          <p className="text-3xl font-black">{countdown(new Date(next.start_iso).getTime() - now)}</p>
        </div>
      )}
    </div>
  );
}

// Stats tab: how each show has been doing. Connections are totals, not a filtered "people" estimate.
export function ShowStatsPanel({ stats, loading, error }) {
  const { t } = useI18n();
  if (loading && !stats) return <p className="text-neutral-400 py-10 text-center">{t('Loading…')}</p>;
  if (error) return <p className="text-neutral-500 py-10 text-center">{t('Stats are not available right now.')}</p>;
  const list = stats?.shows || [];
  if (!list.length) return <p className="text-neutral-400 py-10 text-center">{t('No shows assigned to you yet.')}</p>;

  return (
    <div className="space-y-8">
      {list.map((s) => (
        <section key={s.show_id}>
          <h3 className="text-black font-bold text-lg mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-purple-700" /> {s.show_name}
          </h3>
          {!s.last ? (
            <p className="text-neutral-400 text-sm bg-neutral-50 border border-neutral-200 rounded-xl p-4">
              {t('Stats start with your next show. Once it finishes, you will see how many people connected.')}
            </p>
          ) : (
            <>
              <p className="text-neutral-500 text-xs font-semibold uppercase tracking-wide mb-2">
                {t('Last show')} · {formatAirDate(s.last.date)}
              </p>
              {s.last.best_moment && (
                <div className="mb-4 flex items-start gap-3 rounded-xl bg-purple-50 border border-purple-200 p-4 text-sm">
                  <Zap size={18} className="text-purple-700 shrink-0 mt-0.5" />
                  <p className="text-purple-950">
                    <strong>{t('Best moment')}:</strong> {s.last.best_moment.joins} {t('new connections in 5 minutes around')} {fmtTime(s.last.best_moment.at)} ET
                    {s.last.best_moment.song ? <> {t('while')} <strong>{s.last.best_moment.song}</strong>{s.last.best_moment.artist ? ` — ${s.last.best_moment.artist}` : ''} {t('was playing')}</> : null}.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Tile label={t('Total connections')} value={s.last.connections}>
                  <Delta now={s.last.connections} before={s.previous?.connections} />
                </Tile>
                <Tile
                  label={t('Average time listened')}
                  value={s.last.avg_listen_min ? `${Math.round(s.last.avg_listen_min)} min` : '—'}
                />
                <Tile label={t('Last 30 days')} value={s.last30.connections} sub={`${s.last30.airings} ${t('shows')} · ${s.last30.avg_connections} ${t('per show')}`} />
                <Tile label={t('Best show')} value={s.last30.best_connections} />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="bg-white rounded-xl border border-neutral-200 p-4">
                  <p className="text-neutral-500 text-xs font-bold uppercase tracking-wide mb-3">{t('Connections per show')}</p>
                  <div className="h-44">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={s.recent.map((r) => ({ ...r, label: formatAirDate(r.date) }))} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="connections" name={t('Connections')} fill="#7e22ce" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>


              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <div className="bg-white rounded-xl border border-neutral-200 p-4">
                  <p className="text-neutral-500 text-xs font-bold uppercase tracking-wide mb-3">{t('Where listeners are')}</p>
                  {(s.last.countries || []).length === 0 ? (
                    <p className="text-neutral-400 text-sm">{t('No data yet.')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {(() => {
                        const max = Math.max(...s.last.countries.map((c) => c.count), 1);
                        const total = s.last.countries.reduce((n, c) => n + c.count, 0) || 1;
                        return s.last.countries.map((c) => (
                          <li key={c.country} className="flex items-center gap-3 text-sm">
                            <span className="w-32 font-semibold text-neutral-700 shrink-0 truncate">{countryName(c.country)}</span>
                            <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden">
                              <div className="h-full bg-purple-600 rounded-full" style={{ width: `${(c.count / max) * 100}%` }} />
                            </div>
                            <span className="w-10 text-right text-neutral-500 text-xs">{Math.round((c.count / total) * 100)}%</span>
                          </li>
                        ));
                      })()}
                    </ul>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-neutral-200 p-4">
                  <p className="text-neutral-500 text-xs font-bold uppercase tracking-wide mb-3">{t('Songs that kept people')} <span className="normal-case font-normal text-neutral-400">({t('joins minus leaves')})</span></p>
                  <SongRows rows={s.last.songs?.kept} positive />
                  <p className="text-neutral-500 text-xs font-bold uppercase tracking-wide mt-4 mb-3">{t('Songs people left on')}</p>
                  <SongRows rows={s.last.songs?.lost} />
                </div>
              </div>
            </>
          )}
        </section>
      ))}

      <p className="text-neutral-400 text-xs flex gap-2 items-start">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>
          {t('A connection is one device tuning in to the station stream, from Openstream, the apps or the website. Total connections counts everyone who was connected at the start plus everyone who joined during the show.')}
        </span>
      </p>
    </div>
  );
}
